import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useLeClub from "./hooks/useLeClub";

import ClubDrawer from "./components/ClubDrawer";
import ClubHero from "./components/ClubHero";
import ClubStats from "./components/ClubStats";
import ClubTabs from "./components/ClubTabs";

import TeamPanel from "./components/TeamPanel";
import TeamMemberDetails from "./components/TeamMemberDetails";
import TeamMemberForm from "./components/TeamMemberForm";

import UsersPanel from "./components/UsersPanel";
import UserDetails from "./components/UserDetails";
import UserForm from "./components/UserForm";

import HistoryPanel from "./components/HistoryPanel";
import HistoryForm from "./components/HistoryForm";
import useScrollToTop from "../../../../hooks/useScrollToTop";

export default function LeClub() {
  useScrollToTop();
  const {
    activeTab,
    setActiveTab,
    loading,
    saving,
    filters,
    setFilter,
    teamMembers,
    filteredTeamMembers,
    users,
    filteredUsers,
    settings,
    stats,
    drawerMode,
    selectedTeamMember,
    selectedUser,
    deleteCandidate,
    actions,
  } = useLeClub();

  return (
    <div className="space-y-6">
      <ClubHero
        loading={loading}
        stats={stats}
        activeTab={activeTab}
        onChangeTab={setActiveTab}
        onRefresh={actions.refreshAll}
      />

      <ClubStats loading={loading} stats={stats} />

      <ClubTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        stats={stats}
      />

      {activeTab === "team" && (
        <TeamPanel
          loading={loading.team}
          filters={filters.team}
          setFilter={(name, value) => setFilter("team", name, value)}
          teamMembers={teamMembers}
          filteredTeamMembers={filteredTeamMembers}
          deleteCandidate={deleteCandidate}
          onCreate={actions.openCreateTeamDrawer}
          onDetails={actions.openTeamDetailsDrawer}
          onEdit={actions.openEditTeamDrawer}
          onAskDelete={actions.askDeleteTeamMember}
          onCancelDelete={actions.cancelDelete}
          onConfirmDelete={actions.confirmDeleteTeamMember}
          onRefresh={actions.refreshTeamMembers}
        />
      )}

      {activeTab === "users" && (
        <UsersPanel
          loading={loading.users}
          filters={filters.users}
          setFilter={(name, value) => setFilter("users", name, value)}
          users={users}
          filteredUsers={filteredUsers}
          deleteCandidate={deleteCandidate}
          onCreate={actions.openCreateUserDrawer}
          onDetails={actions.openUserDetailsDrawer}
          onEdit={actions.openEditUserDrawer}
          onAskDelete={actions.askDeleteUser}
          onCancelDelete={actions.cancelDelete}
          onConfirmDelete={actions.confirmDeleteUser}
          onRefresh={actions.refreshUsers}
        />
      )}

      {activeTab === "history" && (
        <HistoryPanel
          loading={loading.settings}
          settings={settings}
          onEdit={actions.openHistoryDrawer}
          onRefresh={actions.refreshSettings}
        />
      )}

      <ClubDrawer
        open={Boolean(drawerMode)}
        mode={drawerMode}
        teamMember={selectedTeamMember}
        user={selectedUser}
        onClose={actions.closeDrawer}
      >
        {drawerMode === "team-details" && selectedTeamMember && (
          <TeamMemberDetails
            member={selectedTeamMember}
            onEdit={() => actions.openEditTeamDrawer(selectedTeamMember)}
            onClose={actions.closeDrawer}
          />
        )}

        {drawerMode === "team-create" && (
          <TeamMemberForm
            mode="create"
            saving={saving}
            onSubmit={actions.createTeamMember}
            onClose={actions.closeDrawer}
          />
        )}

        {drawerMode === "team-edit" && selectedTeamMember && (
          <TeamMemberForm
            mode="edit"
            member={selectedTeamMember}
            saving={saving}
            onSubmit={actions.updateTeamMember}
            onClose={actions.closeDrawer}
          />
        )}

        {drawerMode === "user-details" && selectedUser && (
          <UserDetails
            user={selectedUser}
            onEdit={() => actions.openEditUserDrawer(selectedUser)}
            onClose={actions.closeDrawer}
          />
        )}

        {drawerMode === "user-create" && (
          <UserForm
            mode="create"
            saving={saving}
            onSubmit={actions.createUser}
            onClose={actions.closeDrawer}
          />
        )}

        {drawerMode === "user-edit" && selectedUser && (
          <UserForm
            mode="edit"
            user={selectedUser}
            saving={saving}
            onSubmit={actions.updateUser}
            onClose={actions.closeDrawer}
          />
        )}

        {drawerMode === "history-edit" && (
          <HistoryForm
            settings={settings}
            saving={saving}
            onSubmit={actions.updateHistory}
            onClose={actions.closeDrawer}
          />
        )}
      </ClubDrawer>

      <ToastContainer position="bottom-right" theme="colored" />
    </div>
  );
}